/** Analysis preset configuration */
export interface AnalysisPreset {
  id: string;
  label: string;
  description: string;
  icon: string;
  /** Stockfish search depth per position */
  depth: number;
  /** Number of threads (0 = auto-detect) */
  threads: number;
  /** Hash table size in MB */
  hash: number;
  /** Number of principal variations to compute */
  multiPv: number;
  /** Use the full NNUE engine (108MB) vs lite (7MB) */
  useFullEngine: boolean;
  /** Max continuation steps to pre-compute evals for (on mistakes) */
  continuationDepth: number;
  /** Whether this preset reaches the minimum depth required for verified tactical patterns. */
  verifiesTacticalPatterns: boolean;
  /** Forces the universal single-thread asset for reproducible verifier searches. */
  forceSingleThread?: boolean;
}

const TYPICAL_GAME_SECONDS_AT_FOUR_CORES: Record<AnalysisPreset["id"], number> = {
  quick: 45,
  balanced: 180,
  deep: 720,
};

const FULL_ENGINE_DOWNLOADED_KEY = "chessla:stockfish-18-full-downloaded";

export const hasFullEngineDownload = (): boolean => {
  if (typeof localStorage === "undefined") return false;
  try {
    return localStorage.getItem(FULL_ENGINE_DOWNLOADED_KEY) === "true";
  } catch {
    return false;
  }
};

export const markFullEngineDownloaded = (): void => {
  try {
    localStorage.setItem(FULL_ENGINE_DOWNLOADED_KEY, "true");
  } catch {
    // The engine can still run when browser storage is unavailable.
  }
};

const formatEstimateRange = (lowerSeconds: number, upperSeconds: number): string => {
  if (upperSeconds < 60) return `${Math.round(lowerSeconds / 5) * 5}–${Math.round(upperSeconds / 5) * 5} sec`;
  const lowerMinutes = lowerSeconds / 60;
  const upperMinutes = upperSeconds / 60;
  if (upperMinutes < 10) return `${lowerMinutes.toFixed(1)}–${upperMinutes.toFixed(1)} min`;
  return `${Math.round(lowerMinutes)}–${Math.round(upperMinutes)} min`;
};

export const estimateAnalysisSecondsPerGame = (
  preset: AnalysisPreset,
  deviceCores = typeof navigator === "undefined" ? 4 : (navigator.hardwareConcurrency ?? 4),
  calibratedSeconds?: number | null,
): number => {
  if (calibratedSeconds != null) return calibratedSeconds;
  const capacityFactor = Math.min(3, Math.max(0.5, 4 / deviceCores));
  const memory =
    typeof navigator !== "undefined" && "deviceMemory" in navigator
      ? Number((navigator as Navigator & { deviceMemory?: number }).deviceMemory)
      : undefined;
  const memoryFactor = memory != null && memory <= 4 ? 1.25 : memory != null && memory >= 16 ? 0.9 : 1;
  return TYPICAL_GAME_SECONDS_AT_FOUR_CORES[preset.id] * capacityFactor * memoryFactor;
};

/** A deliberately broad guide; game length and browser load still affect the actual run. */
export const estimateAnalysisTimePerGame = (
  preset: AnalysisPreset,
  deviceCores = typeof navigator === "undefined" ? 4 : (navigator.hardwareConcurrency ?? 4),
  calibratedSeconds?: number | null,
): string => {
  const center = estimateAnalysisSecondsPerGame(preset, deviceCores, calibratedSeconds);
  const spread = calibratedSeconds == null ? 0.4 : 0.25;
  return formatEstimateRange(center * (1 - spread), center * (1 + spread));
};

export const PRESETS: AnalysisPreset[] = [
  {
    id: "quick",
    label: "Quick",
    description: "A fast browser-local engine pass for major evaluation swings and short tactical lines.",
    icon: "♞",
    depth: 12,
    threads: 0, // auto (all cores - 1)
    hash: 64,
    multiPv: 1,
    useFullEngine: false,
    continuationDepth: 5,
    verifiesTacticalPatterns: false,
  },
  {
    id: "balanced",
    label: "Regular",
    description: "A balanced browser-local search with deeper evaluation and verified tactical lines.",
    icon: "♝",
    depth: 16,
    threads: 0, // auto
    hash: 128,
    multiPv: 1,
    useFullEngine: false,
    continuationDepth: 4,
    verifiesTacticalPatterns: true,
  },
  {
    id: "deep",
    label: "Deep",
    description: "The deepest browser-local search for longer candidate lines and difficult tactical positions.",
    icon: "♚",
    depth: 24,
    threads: 0, // auto (max)
    hash: 256,
    multiPv: 3,
    useFullEngine: true,
    continuationDepth: 12,
    verifiesTacticalPatterns: true,
  },
];

export const canRunAnalysisPreset = (
  preset: AnalysisPreset,
  existingDepth: number | null,
  needsTacticalReanalysis: boolean,
): boolean =>
  existingDepth == null ||
  (needsTacticalReanalysis
    ? preset.verifiesTacticalPatterns && preset.depth >= existingDepth
    : preset.depth > existingDepth);

export const resolveAnalysisPreset = (
  preferred: AnalysisPreset,
  existingDepth: number | null,
  needsTacticalReanalysis: boolean,
): AnalysisPreset | undefined =>
  canRunAnalysisPreset(preferred, existingDepth, needsTacticalReanalysis)
    ? preferred
    : PRESETS.find((preset) => canRunAnalysisPreset(preset, existingDepth, needsTacticalReanalysis));

export const resolveNikolaAnalysisPreset = (preset: "regular" | "quick" | "deep"): AnalysisPreset | undefined =>
  PRESETS.find(({ id }) => id === (preset === "regular" ? "balanced" : preset));

/** Resolves thread count: 0 means auto-detect (all cores minus 1 for UI) */
export function resolveThreads(threads: number): number {
  if (!supportsThreadedAnalysis()) return 1;
  if (threads > 0) return threads;
  const cores = navigator.hardwareConcurrency ?? 4;
  return Math.max(1, cores - 1);
}

/**
 * The default Stockfish build uses shared WebAssembly memory for threads.
 * Older iOS Safari versions and pages without cross-origin isolation cannot
 * create that memory, but can still use the bundled single-thread build.
 */
export function supportsThreadedAnalysis(): boolean {
  if (typeof crossOriginIsolated === "undefined" || !crossOriginIsolated || typeof SharedArrayBuffer === "undefined")
    return false;

  try {
    const memory = new WebAssembly.Memory({ initial: 1, maximum: 1, shared: true });
    return memory.buffer instanceof SharedArrayBuffer;
  } catch {
    // Some browsers expose SharedArrayBuffer but cannot create the shared
    // WebAssembly memory required by Stockfish's pthread build.
    return false;
  }
}

/** Minimum browser primitives required by every bundled engine build. */
export function supportsLocalAnalysis(): boolean {
  return typeof Worker !== "undefined" && typeof WebAssembly !== "undefined";
}

/** Gets the Stockfish engine URL based on preset */
export function getEngineUrl(preset: AnalysisPreset): string {
  if (preset.useFullEngine) return "/stockfish/stockfish-18.js";
  if (preset.forceSingleThread) return "/stockfish/stockfish-18-lite-single.js";
  return supportsThreadedAnalysis() ? "/stockfish/stockfish-18-lite.js" : "/stockfish/stockfish-18-lite-single.js";
}
